// src/utils/apiUtils.js (or similar)

import { API, GRAPHQL_AUTH_MODE } from "@aws-amplify/api"; // Need Amplify to get config
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { ApolloLink, split } from "apollo-link";
import { createHttpLink } from "apollo-link-http";
import { WebSocketLink } from "apollo-link-ws"; // v2 import path
import { getMainDefinition } from "apollo-utilities";
import envConfig from "../../envConfig";
import { generateRowId } from "../Utility";
const isWebSocketSubs = true;
const isElectron = !!window?.electronAPI;
let appSyncHost;
const urlObject = new URL(envConfig?.REACT_APP_GRAPHQL_ENDPOINT); // Parse the HTTP URL
if (urlObject.protocol !== "https:") {
  throw new Error("Expected HTTPS URL");
}
appSyncHost = urlObject.host;
let appsyncUrlRealTime;

export const callAsyncFunction = async (asyncFn, ...args) => {
  if (typeof asyncFn !== "function") {
    return null;
  }
  let result;
  if (asyncFn) result = await asyncFn(...args);
  return result;
};

const openWebsocket = () => {
  const payload = {};

  return new Promise((resolve, reject) => {
    AppAuth.getCurrentIdToken()
      .then((token) => {
        const adapt_token = createBase64Header(token, appSyncHost);
        const base64Payload = btoa(JSON.stringify(payload));
        appsyncUrlRealTime = `${envConfig.REACT_APP_REALTIME_ENDPOINT}?header=${adapt_token}&payload=${base64Payload}`;
        const websocketObj = new WebSocket(appsyncUrlRealTime, ["graphql-ws"]);
        resolve(websocketObj);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const runWebsocket = async (query, variables, observer) => {
  const ws = await openWebsocket();
  const sendMessage = (obj) => {
    ws.send(JSON.stringify(obj));
  };

  let initializingMode = true;

  ws.onopen = () => {
    sendMessage({ type: "connection_init" });
  };

  ws.onmessage = async (event) => {
    const data = JSON.parse(event.data);
    if (initializingMode) {
      if (data.type === "connection_ack") {
        // Acknowledge received, start subscribing

        // Get the Cognito User Pool ID token
        const idToken = await AppAuth.getCurrentIdToken(); // Implement this function to retrieve the ID token
        const jsonString = JSON.stringify({
          query: query,
          variables: variables,
        });
        // const wrappedSubscription =
        //   '{"query":"subscription onUpdateWorkspace {\\n onUpdateWorkspace {\\n __typename\\n id\\n }\\n }","variables":{}}';
        sendMessage({
          id: `${generateRowId(4)}`, // Use the ID token from Cognito User Pools
          type: "start",
          payload: {
            data: jsonString,
            extensions: {
              authorization: {
                Authorization: idToken,
                host: appSyncHost,
                "x-amz-user-agent": "aws-amplify/4.7.14 js",
              },
            },
          },
        });

        initializingMode = false;
      }
    } else {
      // Handle subscription data
      if (data.type === "data") {
        const subscriptionData = data;
        observer.next({ value: subscriptionData?.payload });
      } else if (data.type == "ka") {
        if (observer?.connect) observer?.connect();
      }
    }
  };

  ws.onerror = (error) => {
    observer.error(error);
  };

  ws.onclose = () => {
    if (observer?.close) observer?.close();
  };

  return {
    ...ws,
    unsubscribe: () => {
      ws.onopen = () => {};
      ws.onmessage = () => {};
      ws.onerror = () => {};
      ws.onclose = () => {};
      ws.close();
    },
  };
};

const createBase64Header = (authToken, host) => {
  const headerObj = {
    Authorization: authToken,
    host,
  };

  const base64Header = btoa(JSON.stringify(headerObj));

  return base64Header;
};

/**
 * Makes a GraphQL request, handling authentication for both Electron and Web.
 * @param {object} operation The result of graphqlOperation(query, variables)
 * @returns {Promise<object>} The GraphQL response data
 * @throws If the request fails or the user is not authenticated
 */
export const makeGraphQLRequest = async (operation) => {
  if (!operation?.query) {
    throw new Error("Invalid GraphQL operation provided.");
  }

  if (isElectron) {
    const idToken = await AppAuth.getCurrentIdToken();
    if (!idToken) {
      throw new Error("User not authenticated or session expired.");
    }

    const endpoint = envConfig.REACT_APP_GRAPHQL_ENDPOINT;
    if (!endpoint) {
      throw new Error("API endpoint configuration error.");
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken,
      },
      body: JSON.stringify(operation),
    });

    const responseBody = await response.json();

    if (!response.ok || responseBody.errors) {
      const errorMessage =
        responseBody?.errors?.[0]?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    return responseBody;
  } else {
    const idToken = await AppAuth.getCurrentIdToken();
    if (!idToken) {
      throw new Error("User not authenticated or session expired.");
    }

    const response = await API.graphql(
      {
        ...operation,
        authMode: GRAPHQL_AUTH_MODE.AWS_LAMBDA,
      },
      { Authorization: idToken }
    );

    if (response.errors) {
      throw new Error(
        response.errors[0]?.message || "GraphQL request failed"
      );
    }

    return response;
  }
};

let electronAppSyncClient = null;

export const initializeElectronAppSyncClient = () => {
  if (electronAppSyncClient) {
    return electronAppSyncClient;
  }

  const httpUrl = envConfig?.REACT_APP_GRAPHQL_ENDPOINT;

  if (!httpUrl) {
    throw new Error(
      "Missing AppSync HTTP URL configuration for Electron client."
    );
  }

  let appSyncHost;

  AppAuth.getCurrentIdToken()
    .then(() => {
      const urlObject = new URL(httpUrl);
      if (urlObject.protocol !== "https:") {
        throw new Error("Expected HTTPS URL");
      }
      appSyncHost = urlObject.host;
    })
    .catch(() => {
      throw new Error(`Invalid AppSync HTTP URL format: ${httpUrl}`);
    });

  const getToken = async () => {
    try {
      const token = await AppAuth.getCurrentIdToken();
      return token || null;
    } catch (error) {
      return null;
    }
  };

  // --- Create HTTP Link ---
  // Handles Queries and Mutations
  const httpLink = createHttpLink({
    uri: httpUrl, // Use the main GraphQL endpoint URL
    // Headers will be set dynamically via authMiddleware
  });
  let wsLink;
  const extensions = {
    my_ets: "test",
  };
  AppAuth.getCurrentIdToken()
    .then((token) => {
      const adapt_token = createBase64Header(token, appSyncHost);
      wsLink = new WebSocketLink({
        uri: `${envConfig.REACT_APP_REALTIME_ENDPOINT}/graphql?header=${adapt_token}&payload=e30=`,
        options: {
          reconnect: true, // Automatically attempt to reconnect on disconnect
          lazy: true, // Establish connection only when first subscription starts
        },
        connectionParams: {
          // Include the custom extensions object in the connectionParams
          extensions: extensions,
        },
      });
      // --- Create WebSocket Link ---
      // Handles Subscriptions

      // --- Create Authentication Link (Middleware) ---
      // Injects the Authorization header for HTTP requests (Queries/Mutations)
      const removeOperationNameLink = new ApolloLink((operation, forward) => {
        // Remove the operationName from the context
        const context = { ...operation.getContext() };

        // Create a new operation with the modified context
        const modifiedOperation = operation.setContext(context);

        // Forward the modified operation
        return forward(modifiedOperation);
      });

      const authMiddleware = new ApolloLink(async (operation, forward) => {
        // Don't fetch token unnecessarily if it's a subscription (handled by wsLink connectionParams)
        // Note: This optimization might be premature if you have unauth'd queries/mutations too.
        // Keep simple for now: always fetch and set header if available.
        const token = await getToken();

        // Dynamically set the context with authorization header
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers, // Preserve existing headers
            // Add Authorization header only if token exists
            ...(token ? { Authorization: token } : {}),
          },
        }));

        // Proceed to the next link in the chain (httpLink)
        return forward(operation);
      });

      // Combine the links
      const combinedLink = removeOperationNameLink.concat(authMiddleware);
      // --- Split Link ---
      // Directs traffic: subscriptions to wsLink, others to authMiddleware -> httpLink
      const splitLink = split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          const isSubscription =
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription";
          // console.log(`splitLink: Routing operation. Is Subscription? ${isSubscription}`);
          return isSubscription;
        },
        wsLink, // Target for subscriptions
        combinedLink.concat(httpLink) // Target for queries and mutations
      );

      // --- Create Apollo Cache ---
      const cache = new InMemoryCache();
      // --- Initialize Apollo Client (v2) ---
      try {
        electronAppSyncClient = new ApolloClient({
          link: splitLink,
          cache: cache,
        });
        return electronAppSyncClient;
      } catch (error) {
        electronAppSyncClient = null;
        throw error;
      }
    })
    .catch(() => {
      throw new Error(`Invalid token`);
    });
  return electronAppSyncClient;
};

/**
 * Creates a GraphQL subscription, handling authentication for both Electron and Web.
 * @param {object} operation The result of graphqlOperation(query, variables)
 * @param {object} observer An object with next() and error() callbacks.
 * @returns {object} A subscription object with an unsubscribe() method.
 */
export const subscribeGraphQL = (operation, observer) => {
  if (
    !operation?.query ||
    !observer ||
    typeof observer.next !== "function" ||
    typeof observer.error !== "function"
  ) {
    return {
      unsubscribe: () => {},
    };
  }

  if (isWebSocketSubs) {
    return runWebsocket(operation.query, operation.variables, observer);
  } else {
    // --- Web Flow: Use Amplify API ---

    try {
      return new Promise((resolve, reject) => {
        const subscription = API.graphql(operation).subscribe({
          next: (data) => {
            observer.next(data);
          },
          error: (error) => {
            reject(error);
          },
          complete: () => {
            if (observer.complete) observer.complete();
          },
          close: () => {
            if (observer.close) observer.close();
          },
          connect: () => {
            if (observer.connect) observer.connect();
          },
        });
        resolve(subscription);
      });
    } catch (error) {
      observer.error(error);
      return { unsubscribe: () => {} };
    }
  }
};
